import { useNavigate } from 'react-router-dom';
import {
  Box,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { nextStep, overdueDays, type OrderFeedRow } from '@zilian/contracts';
import { Empty, FitTableContainer, money, RowAction, Tag } from './ui';

const shortDate = (v: string) => {
  const d = new Date(v);
  return `${d.getMonth() + 1}月${d.getDate()}日`;
};

const pickupTime = (v: string) => {
  const d = new Date(v);
  return `${shortDate(v)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/**
 * Shared purchase / sales list. Each row leads with where the order stands and whose turn it
 * is, so people can scan for what needs them; clicking a row opens its details.
 */
export default function OrderFeedTable({
  orders,
  postLocal,
  quickStep,
  openLocal,
  side,
  compact,
  fit,
}: {
  orders: OrderFeedRow[];
  postLocal: (id: string) => void;
  /** Does a 登记 / 确认 step in place; false sends the click on to the order. */
  quickStep?: (order: OrderFeedRow) => boolean;
  openLocal: (id: string) => void;
  side?: 'BUY' | 'SELL';
  /** Glance view (dashboard): the delivery date sits under the amount instead of its own column. */
  compact?: boolean;
  /** Full list page: cap the height to the screen so the pager below stays visible. */
  fit?: boolean;
}) {
  const navigate = useNavigate();
  if (!orders.length)
    return (
      <Empty>
        {side
          ? `没有符合条件的${side === 'BUY' ? '采购' : '销售'}记录。`
          : '暂无记录，可新增一笔采购或销售。'}
      </Empty>
    );
  const open = (order: OrderFeedRow) =>
    order.source === 'SHARED' ? navigate(`/orders/${order.id}`) : openLocal(order.id);
  const head = (label: string, align: 'left' | 'right' = 'left') => (
    <TableCell key={label} align={align}>
      {label}
    </TableCell>
  );
  const Container = fit ? FitTableContainer : TableContainer;
  return (
    <Container>
      {/* Status and delivery progress share one column, and the name column absorbs (and
          truncates in) whatever width is left, so the list fits a 1440px dashboard. */}
      <Table
        stickyHeader={fit}
        sx={{
          minWidth: compact ? 600 : side ? 700 : 780,
          '& th': { whiteSpace: 'nowrap' },
          '& th, & td': { px: 1.5 },
          '& th:first-of-type, & td:first-of-type': { pl: 2.5 },
          '& th:last-of-type, & td:last-of-type': { pr: 2.5 },
        }}
      >
        <TableHead>
          <TableRow>
            {head(side ? (side === 'BUY' ? '单号 / 供应商' : '单号 / 客户') : '单号 / 往来单位')}
            {!side && head('方向')}
            {!compact && head('交期')}
            {head(compact ? '金额 / 交期' : '金额', 'right')}
            {head('进度')}
            {head('')}
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map((order) => {
            const step = nextStep(order);
            const late = overdueDays(order, step);
            const multi = (order.line_count ?? 1) > 1;
            const verb = order.side === 'BUY' ? '收' : '发';
            return (
              <TableRow
                key={`${order.source}-${order.id}`}
                hover
                onClick={() => open(order)}
                sx={{
                  cursor: 'pointer',
                  // A slim accent marks rows waiting on this enterprise.
                  '& td:first-of-type': {
                    boxShadow: step.mine ? 'inset 3px 0 0 #e09a2b' : 'none',
                  },
                }}
              >
                <TableCell sx={{ width: '40%', maxWidth: 0, py: 1.75 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 650, minWidth: 0 }} noWrap>
                      {order.peer}
                    </Typography>
                    <Tag tone={order.source === 'SHARED' ? 'info' : 'neutral'}>
                      {order.source === 'SHARED' ? '协同' : '本企业登记'}
                    </Tag>
                  </Box>
                  <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.5 }} noWrap>
                    {order.number}
                    {order.sku_snapshot &&
                      ` · ${order.sku_snapshot.name}${multi ? ` 等 ${order.line_count} 种` : ''}`}
                  </Typography>
                </TableCell>
                {!side && (
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {order.side === 'BUY' ? '采购' : '销售'}
                  </TableCell>
                )}
                {!compact && (
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography sx={{ fontSize: 13 }}>{shortDate(order.delivery_date)}</Typography>
                    {late > 0 && (
                      <Typography color="error" sx={{ fontSize: 12, fontWeight: 600 }}>
                        逾期 {late} 天
                      </Typography>
                    )}
                  </TableCell>
                )}
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 650 }}>
                  {/* Left out by the server on a side the member sees no money on. */}
                  {order.amount !== undefined && money(order.amount)}
                  {compact && (
                    <Typography
                      color={late > 0 ? 'error' : 'text.secondary'}
                      sx={{ fontSize: 11.5, fontWeight: late > 0 ? 600 : 400, mt: 0.5 }}
                    >
                      {late > 0 ? `逾期 ${late} 天` : `${shortDate(order.delivery_date)}交`}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 150 }}>
                  <Tag tone={step.tone}>{step.label}</Tag>
                  {step.label !== '已取消' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                      <LinearProgress
                        variant="determinate"
                        value={step.progress}
                        color={step.progress >= 100 ? 'success' : 'primary'}
                        sx={{ width: 72, height: 4, borderRadius: 1, bgcolor: '#edf1f2' }}
                      />
                      <Typography color="text.secondary" sx={{ fontSize: 11.5 }}>
                        {order.source === 'LOCAL' && multi
                          ? `完成 ${order.lines_done ?? 0}/${order.line_count} 种`
                          : order.source === 'LOCAL'
                            ? `已${order.side === 'BUY' ? '入' : '出'}库 ${step.progress}%`
                            : `已${verb} ${step.progress}%`}
                        {/* Whether the driver has collected what was shipped; no extra column. */}
                        {order.pickup?.state === 'WAITING' && (
                          <Box component="span" sx={{ color: 'warning.dark', fontWeight: 600 }}>
                            {' '}
                            · 待提货
                          </Box>
                        )}
                        {order.pickup?.state === 'TAKEN' && (
                          <Tooltip
                            title={`${[order.pickup.name || '司机', order.pickup.plate].filter(Boolean).join(' ')} · ${pickupTime(order.pickup.at!)} 扫码提货`}
                          >
                            <Box component="span"> · 已提货</Box>
                          </Tooltip>
                        )}
                      </Typography>
                    </Box>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <RowAction
                    label={step.mine ? step.actionLabel : undefined}
                    onClick={() =>
                      step.action === 'POST'
                        ? postLocal(order.id)
                        : quickStep?.(order) || open(order)
                    }
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Container>
  );
}
